/**
 * Dashboard Page - Main landing page after login
 * Shows KPI metrics, recent projects, and quick actions
 */

import {
  BarChart3,
  TrendingUp,
  AlertTriangle,
  XCircle,
  Plus,
  FolderOpen,
  ArrowRight,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import { Card, CardTitle } from '@/components/ui/Card';
import {
  useAuth,
  useRecentProjects,
} from '@/hooks';
import { useHomeMetrics } from '@/hooks/useDashboard';
import { cn } from '@/lib/utils';
import type { Project } from '@/types';

// ============================================================================
// SKELETON COMPONENTS
// ============================================================================

function MetricCardSkeleton() {
  return (
    <Card variant="metric">
      <div className="flex items-start justify-between">
        <div className="space-y-3 flex-1">
          <div className="h-3 w-24 bg-surface-subtle rounded animate-pulse" />
          <div className="h-8 w-16 bg-surface-subtle rounded animate-pulse" />
        </div>
        <div className="h-5 w-5 bg-surface-subtle rounded animate-pulse" />
      </div>
    </Card>
  );
}

function ProjectCardSkeleton() {
  return (
    <Card>
      <div className="space-y-4">
        <div className="h-5 w-3/4 bg-surface-subtle rounded animate-pulse" />
        <div className="space-y-2">
          <div className="h-3 w-1/2 bg-surface-subtle rounded animate-pulse" />
          <div className="h-3 w-1/3 bg-surface-subtle rounded animate-pulse" />
        </div>
      </div>
    </Card>
  );
}

// ============================================================================
// METRIC CARD COMPONENT
// ============================================================================

interface MetricCardProps {
  title: string;
  value: number;
  icon: ReactNode;
  badge?: {
    label: string;
    variant: 'success' | 'warning' | 'danger';
  };
}

function MetricCard({ title, value, icon, badge }: MetricCardProps) {
  const badgeStyles = {
    success: 'bg-success-soft text-success',
    warning: 'bg-warning-soft text-warning',
    danger: 'bg-danger-soft text-danger',
  };

  return (
    <Card variant="metric">
      <div className="flex items-start justify-between">
        <div className="space-y-3 flex-1">
          <CardTitle>{title}</CardTitle>
          <div className="flex items-end gap-3">
            <div className="text-3xl font-bold text-primary leading-tight tracking-tight">
              {value.toLocaleString()}
            </div>
            {badge && (
              <div
                className={cn(
                  'inline-flex items-center px-3 h-6 rounded-pill text-xs font-bold uppercase tracking-wider',
                  badgeStyles[badge.variant]
                )}
              >
                {badge.label}
              </div>
            )}
          </div>
        </div>
        <div className="text-secondary opacity-70 flex-shrink-0">
          {icon}
        </div>
      </div>
    </Card>
  );
}

// ============================================================================
// PROJECT CARD COMPONENT
// ============================================================================

interface ProjectCardProps {
  project: Project;
}

function ProjectCard({ project }: ProjectCardProps) {
  const updatedDate = new Date(project.updatedAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <Link to={`/projects/${project.projectId}`}>
      <Card className="transition-all hover:border-border-strong hover:shadow-md cursor-pointer group">
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h3 className="text-md font-semibold text-primary truncate group-hover:text-brand">
                {project.name}
              </h3>
              {project.brandName && (
                <p className="text-xs text-muted mt-1">
                  {project.brandName}
                </p>
              )}
            </div>
            <ArrowRight className="h-4 w-4 text-muted group-hover:text-brand transition-colors flex-shrink-0" />
          </div>

          <div className="flex items-center gap-6 text-xs text-secondary">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-3.5 w-3.5" />
              <span>
                {project.creativeCount ?? 0}{' '}
                {(project.creativeCount ?? 0) === 1 ? 'creative' : 'creatives'}
              </span>
            </div>
            <div className="text-muted">
              Updated {updatedDate}
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
}

// ============================================================================
// EMPTY STATE COMPONENT
// ============================================================================

function EmptyProjectsState() {
  return (
    <div className="text-center py-12">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-surface-subtle mb-4">
        <BarChart3 className="h-8 w-8 text-muted" />
      </div>
      <h3 className="text-lg font-semibold text-primary mb-2">
        No projects yet
      </h3>
      <p className="text-sm text-secondary mb-6 max-w-sm mx-auto">
        Create your first project to start analyzing video creatives and making data-driven decisions.
      </p>
      <Button
        leftIcon={<Plus className="h-4 w-4" />}
      >
        Create First Project
      </Button>
    </div>
  );
}

// ============================================================================
// MAIN DASHBOARD COMPONENT
// ============================================================================

export default function Dashboard() {
  const { user, currentTeam } = useAuth();
  const { data: recentProjects, isLoading: projectsLoading } = useRecentProjects(
    currentTeam?.teamId,
    4
  );
  const { data: homeMetrics, isLoading: metricsLoading } = useHomeMetrics();

  const hasProjects = recentProjects && recentProjects.length > 0;

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex items-start justify-between gap-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted mb-2">
            DASHBOARD
          </div>
          <h1 className="text-4xl font-black text-primary leading-tight tracking-tight">
            Welcome back{user?.firstName ? `, ${user.firstName}` : (user?.name ? `, ${user.name.split(' ')[0]}` : '')}
          </h1>
        </div>
        <Button
          leftIcon={<Plus className="h-4 w-4" />}
        >
          New Project
        </Button>
      </div>

      {/* KPI Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metricsLoading ? (
          <>
            <MetricCardSkeleton />
            <MetricCardSkeleton />
            <MetricCardSkeleton />
            <MetricCardSkeleton />
          </>
        ) : (
          <>
            <MetricCard
              title="Total Creatives"
              value={homeMetrics?.total ?? 0}
              icon={<BarChart3 className="h-5 w-5" />}
            />
            <MetricCard
              title="To Scale"
              value={homeMetrics?.scale ?? 0}
              icon={<TrendingUp className="h-5 w-5" />}
              badge={{
                label: 'Scale',
                variant: 'success',
              }}
            />
            <MetricCard
              title="To Fix"
              value={homeMetrics?.fix ?? 0}
              icon={<AlertTriangle className="h-5 w-5" />}
              badge={{
                label: 'Fix',
                variant: 'warning',
              }}
            />
            <MetricCard
              title="To Kill"
              value={homeMetrics?.kill ?? 0}
              icon={<XCircle className="h-5 w-5" />}
              badge={{
                label: 'Kill',
                variant: 'danger',
              }}
            />
          </>
        )}
      </div>

      {/* Recent Projects Section */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-primary">Recent Projects</h2>
          {hasProjects && (
            <Link
              to="/projects"
              className="text-sm font-semibold text-brand hover:text-brand-hover transition-colors inline-flex items-center gap-2"
            >
              View All Projects
              <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>

        {projectsLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ProjectCardSkeleton />
            <ProjectCardSkeleton />
            <ProjectCardSkeleton />
            <ProjectCardSkeleton />
          </div>
        ) : hasProjects ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {recentProjects.map((project) => (
              <ProjectCard key={project.projectId} project={project} />
            ))}
          </div>
        ) : (
          <Card>
            <EmptyProjectsState />
          </Card>
        )}
      </div>

      {/* Quick Actions Section */}
      {hasProjects && (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-primary">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/projects/new">
              <Card className="transition-all hover:border-border-strong hover:shadow-md cursor-pointer group">
                <div className="flex items-start gap-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-brand/10 text-brand flex-shrink-0 group-hover:bg-brand group-hover:text-inverse transition-colors">
                    <Plus className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-md font-semibold text-primary mb-1 group-hover:text-brand transition-colors">
                      New Project
                    </h3>
                    <p className="text-xs text-secondary">
                      Create a new project for your brand
                    </p>
                  </div>
                </div>
              </Card>
            </Link>

            <Link to="/projects">
              <Card className="transition-all hover:border-border-strong hover:shadow-md cursor-pointer group">
                <div className="flex items-start gap-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-info-soft text-info flex-shrink-0 group-hover:bg-info group-hover:text-inverse transition-colors">
                    <FolderOpen className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-md font-semibold text-primary mb-1 group-hover:text-info transition-colors">
                      Browse Projects
                    </h3>
                    <p className="text-xs text-secondary">
                      Manage projects, upload and compare creatives
                    </p>
                  </div>
                </div>
              </Card>
            </Link>

            <Link to="/dashboard">
              <Card className="transition-all hover:border-border-strong hover:shadow-md cursor-pointer group">
                <div className="flex items-start gap-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-success-soft text-success flex-shrink-0 group-hover:bg-success group-hover:text-inverse transition-colors">
                    <BarChart3 className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-md font-semibold text-primary mb-1 group-hover:text-success transition-colors">
                      View Analytics
                    </h3>
                    <p className="text-xs text-secondary">
                      Explore portfolio health and creative trends
                    </p>
                  </div>
                </div>
              </Card>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
